import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/types';
import StatusBadge from './StatusBadge';

interface Props {
  article: Article;
  variant?: 'default' | 'compact' | 'hero';
}

export default function ArticleCard({ article, variant = 'default' }: Props) {
  const href = `/article/${article.slug}`;

  if (variant === 'compact') {
    return (
      <article className="group flex gap-3 border-b border-background-200 pb-4 last:border-b-0 last:pb-0">
        <Link to={href} className="shrink-0 w-20 h-20 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} - ${article.categoryName}网曝黑料合集配图`}
            title={`${article.title} 网曝黑料 时间线`}
            className="w-20 h-20 object-cover object-top"
          />
        </Link>
        <div className="min-w-0">
          <h3 className="text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors">
            <Link to={href} className="line-clamp-2">
              {article.title}
            </Link>
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500"></i>
              {article.heat}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'hero') {
    return (
      <article className="group relative overflow-hidden rounded-lg">
        <Link to={href} className="block">
          <div className="relative w-full h-[300px] md:h-[440px] bg-background-300">
            <img
              src={article.cover}
              alt={`${article.title} - 网曝黑料合集封面`}
              title={`${article.title} 黑料时间线整理`}
              className="w-full h-[300px] md:h-[440px] object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-transparent"></div>
          </div>
          <div className="absolute bottom-0 left-0 w-full p-5 md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-0.5 text-xs font-medium text-background-50">
                {article.categoryName}
              </span>
              <StatusBadge status={article.status} className="bg-background-50/90" />
            </div>
            <h3 className="mt-4 font-heading text-xl md:text-3xl font-bold leading-snug text-background-50 group-hover:text-accent-300 transition-colors">
              {article.title}
            </h3>
            <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 md:block">
              {article.excerpt}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.date} · {article.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-accent-300"></i>
                {article.heat} 热度
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-stack-line"></i>
                {article.timeline.length} 个时间节点
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors">
      <Link to={href} className="block overflow-hidden">
        <div className="w-full h-44 bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} - ${article.categoryName}爆料合集配图`}
            title={`${article.title} 黑料合集`}
            className="w-full h-44 object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
            {article.categoryName}
          </span>
          <StatusBadge status={article.status} />
        </div>
        <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">
          <Link to={href} className="line-clamp-2">
            {article.title}
          </Link>
        </h3>
        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-auto pt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500"></i>
            {article.heat}
          </span>
          <span>{article.date}</span>
        </div>
      </div>
    </article>
  );
}